import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface LhExecutionCache {
  [key: string]: any; // Cache entries (key = locationId:timeframe)
}

interface HomeScreenState {
  ciDashboardData: any;
  /**
   * @deprecated Use lhExecutionData instead. This is maintained for backward compatibility.
   */
  metricsData: any;
  lhExecutionData: any;
  lhExecutionCache: LhExecutionCache; // Cache for different timeframes and locations
  trainingEssentialsData: any;
  eventsData: any;
  externalLinksData: any;
  changeLogData: any;
  bannerData: any;
  announcementBannerData: any;
  announcementBannerDismissed: boolean;
  loading: {
    ciDashboard: boolean;
    /**
     * @deprecated Use loading.lhExecution instead. This is maintained for backward compatibility.
     */
    metrics: boolean;
    lhExecution: boolean;
    trainingEssentials: boolean;
    events: boolean;
    externalLinks: boolean;
    changeLog: boolean;
    banner: boolean;
    announcementBanner: boolean;
  };
  error: {
    ciDashboard: string | null;
    /**
     * @deprecated Use error.lhExecution instead. This is maintained for backward compatibility.
     */
    metrics: string | null;
    lhExecution: string | null;
    trainingEssentials: string | null;
    events: string | null;
    externalLinks: string | null;
    changeLog: string | null;
    banner: string | null;
    announcementBanner: string | null;
  };
}

const initialState: HomeScreenState = {
  ciDashboardData: null,
  metricsData: null,
  lhExecutionData: null,
  lhExecutionCache: {}, // Initialize empty cache object
  trainingEssentialsData: null,
  eventsData: null,
  externalLinksData: null,
  changeLogData: null,
  bannerData: null,
  announcementBannerData: null,
  announcementBannerDismissed: false,
  loading: {
    ciDashboard: false,
    metrics: false,
    lhExecution: false,
    trainingEssentials: false,
    events: false,
    externalLinks: false,
    changeLog: false,
    banner: false,
    announcementBanner: false,
  },
  error: {
    ciDashboard: null,
    metrics: null,
    lhExecution: null,
    trainingEssentials: null,
    events: null,
    externalLinks: null,
    changeLog: null,
    banner: null,
    announcementBanner: null,
  },
};

const homescreenSlice = createSlice({
  name: "homescreen",
  initialState,
  reducers: {
    setCIDashboardData: (state, action: PayloadAction<any>) => {
      state.ciDashboardData = action.payload;
      state.loading.ciDashboard = false;
      state.error.ciDashboard = null;
    },
    /**
     * @deprecated Use setLHExecutionData instead. This is maintained for backward compatibility.
     */
    setMetricsData: (state, action: PayloadAction<any>) => {
      state.metricsData = action.payload;
      state.loading.metrics = false;
      state.error.metrics = null;
    },
    setLHExecutionData: (state, action: PayloadAction<any>) => {
      state.lhExecutionData = action.payload;
      // For backward compatibility, also update metricsData
      state.metricsData = action.payload;

      // Add to cache if we have location and timeframe data
      if (
        action.payload &&
        action.payload.location &&
        action.payload.timeframe
      ) {
        const locationId = action.payload.location.locationId;
        const timeframe = action.payload.timeframe;
        const cacheKey = `${locationId}:${timeframe}`;
        state.lhExecutionCache[cacheKey] = action.payload;
        console.log(`Cached LH Execution data for ${cacheKey}`);
      }

      state.loading.lhExecution = false;
      state.loading.metrics = false; // Update metrics loading for backward compatibility
      state.error.lhExecution = null;
      state.error.metrics = null; // Update metrics error for backward compatibility
    },
    setCIDashboardLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.ciDashboard = action.payload;
    },
    /**
     * @deprecated Use setLHExecutionLoading instead. This is maintained for backward compatibility.
     */
    setMetricsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.metrics = action.payload;
    },
    setLHExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.lhExecution = action.payload;
      state.loading.metrics = action.payload; // Update metrics loading for backward compatibility
    },
    setCIDashboardError: (state, action: PayloadAction<string>) => {
      state.error.ciDashboard = action.payload;
      state.loading.ciDashboard = false;
    },
    /**
     * @deprecated Use setLHExecutionError instead. This is maintained for backward compatibility.
     */
    setMetricsError: (state, action: PayloadAction<string>) => {
      state.error.metrics = action.payload;
      state.loading.metrics = false;
    },
    setLHExecutionError: (state, action: PayloadAction<string | null>) => {
      state.error.lhExecution = action.payload;
      state.error.metrics = action.payload; // Update metrics error for backward compatibility
      state.loading.lhExecution = false;
      state.loading.metrics = false; // Update metrics loading for backward compatibility
    },
    clearLHExecutionCache: (state) => {
      state.lhExecutionCache = {};
      console.log("LH Execution cache cleared");
    },
    clearLHExecutionCacheForLocation: (
      state,
      action: PayloadAction<string>
    ) => {
      const locationId = action.payload;
      // Remove all cache entries for this location
      Object.keys(state.lhExecutionCache).forEach((key) => {
        if (key.startsWith(`${locationId}:`)) {
          delete state.lhExecutionCache[key];
        }
      });
      console.log(`LH Execution cache cleared for location: ${locationId}`);
    },
    // Training Essentials actions (replaces SharePoint safety)
    setTrainingEssentialsData: (state, action: PayloadAction<any>) => {
      state.trainingEssentialsData = action.payload;
      state.loading.trainingEssentials = false;
      state.error.trainingEssentials = null;
    },
    setTrainingEssentialsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.trainingEssentials = action.payload;
    },
    setTrainingEssentialsError: (state, action: PayloadAction<string | null>) => {
      state.error.trainingEssentials = action.payload;
      state.loading.trainingEssentials = false;
    },
    setEventsData: (state, action: PayloadAction<any>) => {
      state.eventsData = action.payload;
      state.loading.events = false;
      state.error.events = null;
    },
    setEventsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.events = action.payload;
    },
    setEventsError: (state, action: PayloadAction<string | null>) => {
      state.error.events = action.payload;
      state.loading.events = false;
    },
    setExternalLinksData: (state, action: PayloadAction<any>) => {
      state.externalLinksData = action.payload;
      state.loading.externalLinks = false;
      state.error.externalLinks = null;
    },
    setExternalLinksLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.externalLinks = action.payload;
    },
    setExternalLinksError: (state, action: PayloadAction<string | null>) => {
      state.error.externalLinks = action.payload;
      state.loading.externalLinks = false;
    },
    // Change log actions
    setChangeLogData: (state, action: PayloadAction<any>) => {
      state.changeLogData = action.payload;
      state.loading.changeLog = false;
      state.error.changeLog = null;
    },
    setChangeLogLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.changeLog = action.payload;
    },
    setChangeLogError: (state, action: PayloadAction<string | null>) => {
      state.error.changeLog = action.payload;
      state.loading.changeLog = false;
    },
    setAnnouncementBannerDismissed: (state, action: PayloadAction<boolean>) => {
      state.announcementBannerDismissed = action.payload;
    },
    // Announcement Banner actions (from /admin/homepage/banners API)
    setAnnouncementBannerData: (state, action: PayloadAction<any>) => {
      state.announcementBannerData = action.payload;
      state.loading.announcementBanner = false;
      state.error.announcementBanner = null;
    },
    setAnnouncementBannerLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.announcementBanner = action.payload;
    },
    setAnnouncementBannerError: (state, action: PayloadAction<string | null>) => {
      state.error.announcementBanner = action.payload;
      state.loading.announcementBanner = false;
    },
    // Banner actions
    setBannerData: (state, action: PayloadAction<any>) => {
      state.bannerData = action.payload;
      state.loading.banner = false;
      state.error.banner = null;
    },
    setBannerLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.banner = action.payload;
    },
    setBannerError: (state, action: PayloadAction<string | null>) => {
      state.error.banner = action.payload;
      state.loading.banner = false;
    },
  },
});

export const {
  setCIDashboardData,
  setMetricsData,
  setLHExecutionData,
  setCIDashboardLoading,
  setMetricsLoading,
  setLHExecutionLoading,
  setCIDashboardError,
  setMetricsError,
  setLHExecutionError,
  clearLHExecutionCache,
  clearLHExecutionCacheForLocation,
  // Training Essentials actions
  setTrainingEssentialsData,
  setTrainingEssentialsLoading,
  setTrainingEssentialsError,
  setEventsData,
  setEventsLoading,
  setEventsError,
  setExternalLinksData,
  setExternalLinksLoading,
  setExternalLinksError, // Change log actions
  setChangeLogData,
  setChangeLogLoading,
  setChangeLogError,
  // Announcement Banner actions
  setAnnouncementBannerDismissed,
  setAnnouncementBannerData,
  setAnnouncementBannerLoading,
  setAnnouncementBannerError,
  // Banner actions
  setBannerData,
  setBannerLoading,
  setBannerError,
} = homescreenSlice.actions;

export default homescreenSlice.reducer;
